import { useEffect } from "react";
import { ConfirmationDialog } from "ui/confirmation-dialog";
import SelectedItem from "components/partials/selected-items/selected-item/selected-item";
import type { DialogIntent } from "ui/dialog";
import type { TouchpointAttributes } from "hooks/api/use-touchpoints";

interface SelectedTouchpointsConfirmationModalProps {
  title: string;
  description?: string;
  submitButtonLabel: string;
  onCloseCallback: () => void;
  onConfirmCallback: () => void;
  selectedList: TouchpointAttributes[];
  isSubmitDisabled: boolean;
  isSubmitting: boolean;
  variant?: Extract<DialogIntent, "warning" | "error">;
}

const SelectedTouchpointsConfirmationModal = ({
  title,
  description,
  submitButtonLabel,
  onCloseCallback,
  onConfirmCallback,
  selectedList,
  isSubmitDisabled,
  isSubmitting,
  variant = "warning",
}: SelectedTouchpointsConfirmationModalProps) => {
  const hasItems = (selectedList?.length ?? 0) > 0;

  useEffect(() => {
    if (!hasItems) {
      onCloseCallback();
    }
  }, [hasItems, onCloseCallback]);

  const handleConfirm = () => {
    if (!hasItems) return;
    onConfirmCallback();
  };

  const hasScrollableList = selectedList.length > 5;
  const listClassName = hasScrollableList
    ? "flex flex-col gap-2 max-h-[130px] overflow-y-auto custom-scroll"
    : "flex flex-col gap-2 max-h-[130px] overflow-y-visible";

  const selectedItemsContent = hasItems ? (
    selectedList.map((item) => (
      <SelectedItem key={item.id} name={item.name} type={item.type} />
    ))
  ) : (
    <span className="text-gray-500 text-sm">No items selected yet.</span>
  );

  return (
    <ConfirmationDialog
      open
      onClose={onCloseCallback}
      onConfirm={handleConfirm}
      intent={variant}
      headline={title}
      message={description}
      confirmButtonText={submitButtonLabel}
      disabled={isSubmitDisabled || isSubmitting}
      closable={!isSubmitting}
    >
      <div className="flex flex-col gap-2">
        <h2 className="mt-2">Selected items</h2>
        <div className={listClassName}>{selectedItemsContent}</div>
      </div>
    </ConfirmationDialog>
  );
};

export default SelectedTouchpointsConfirmationModal;
